import { type BatchItem, type ReportRequest, type ReportRow, postReportBatch } from './reportingV2.api';

/**
 * Coalesces report queries fired within the same tick into a single
 * `/reporting-2.0/report-batch` call. Every widget keeps dispatching its own
 * `fetchReportWidget({ key, request })`; this layer transparently batches them,
 * so a dashboard load (or a filter change that refetches every widget) is ONE
 * HTTP round trip instead of ~12. Results are matched back to callers by index
 * (robust to two widgets sharing a key), and the shared `freshness` payload is
 * handed to every caller in the batch.
 *
 * A 0ms timer is the coalescing window: React flushes all mount/update effects
 * synchronously before the timer fires, so they land in one batch.
 */
export interface WidgetResult {
  rows: ReportRow[];
  /** Size of the whole result set, not this page — drives the grid's pager. */
  totalRows: number;
  freshness: Record<string, string>;
  /** See ReportBatchResponse.data.results[].ignored_filters. */
  ignoredFilters: string[];
  /** See ReportBatchResponse.data.results[].restricted_fields. */
  restrictedFields: string[];
  /** See ReportBatchResponse.data.results[].truncated_fields. */
  truncatedFields: string[];
}

interface Pending extends BatchItem {
  resolve: (result: WidgetResult) => void;
  reject: (error: unknown) => void;
}

let queue: Pending[] = [];
let scheduled = false;

/**
 * Widgets per HTTP request.
 *
 * One batch of everything is the worst case for perceived speed: a page load
 * enqueues ~15 Snowflake queries, and because they share a single response
 * every widget paints at the moment the SLOWEST one returns. Each widget has a
 * skeleton, but nothing can stream, so the page sits blank for the duration of
 * its worst query.
 *
 * Splitting into groups keeps most of the round-trip saving (15 requests -> 4)
 * while letting each group paint as it lands, so the page fills in
 * progressively. Groups are dispatched in parallel; a slow query now delays
 * only its own group.
 */
const GROUP_SIZE = 4;

const sendGroup = async (batch: Pending[]): Promise<void> => {
  try {
    const response = await postReportBatch(batch.map(({ key, request }) => ({ key, request })));
    const results = response.data.results ?? [];
    batch.forEach((pending, index) => {
      pending.resolve({
        rows: results[index]?.rows ?? [],
        totalRows: results[index]?.total_rows ?? 0,
        freshness: response.freshness ?? {},
        ignoredFilters: results[index]?.ignored_filters ?? [],
        restrictedFields: results[index]?.restricted_fields ?? [],
        truncatedFields: results[index]?.truncated_fields ?? [],
      });
    });
  } catch (error) {
    for (const pending of batch) pending.reject(error);
  }
};

const flush = async (): Promise<void> => {
  const batch = queue;
  queue = [];
  scheduled = false;
  if (batch.length === 0) return;

  const groups: Pending[][] = [];
  for (let i = 0; i < batch.length; i += GROUP_SIZE) groups.push(batch.slice(i, i + GROUP_SIZE));

  // Independent on purpose: one group failing must not reject the others.
  await Promise.all(groups.map(sendGroup));
};

/** Enqueue a widget query; resolves with that widget's rows once the batch returns. */
export const enqueueReport = (key: string, request: ReportRequest): Promise<WidgetResult> =>
  new Promise<WidgetResult>((resolve, reject) => {
    queue.push({ key, request, resolve, reject });
    if (!scheduled) {
      scheduled = true;
      setTimeout(flush, 0);
    }
  });
